import { createTheme, ThemeOptions } from '@mui/material/styles';
import { red, green, orange } from '@mui/material/colors';

// DrSync Color Palette - Apple-inspired design
export const colors = {
  navy: 'rgb(47, 65, 86)',
  teal: 'rgb(86, 124, 141)',
  skyBlue: 'rgb(200, 217, 230)',
  beige: 'rgb(245, 239, 235)',
  white: 'rgb(255, 255, 255)',
  warning: '#f59e0b',
  border: '#e5e7eb',
  textSecondary: '#6b7280',
  backgroundLight: '#f9fafb',
};

// Define custom colors for the healthcare theme
const customColors = {
  primary: {
    light: colors.teal,
    main: colors.navy,
    dark: 'rgb(30, 42, 56)',
    contrastText: colors.white,
  },
  secondary: {
    light: colors.skyBlue,
    main: colors.teal,
    dark: 'rgb(60, 90, 105)',
    contrastText: colors.white,
  },
  success: {
    light: green[300],
    main: green[500],
    dark: green[700],
    contrastText: colors.white,
  },
  warning: {
    light: orange[300],
    main: orange[500],
    dark: orange[700],
    contrastText: colors.white,
  },
  error: {
    light: red[300],
    main: red[500],
    dark: red[700],
    contrastText: colors.white,
  },
  info: {
    light: colors.skyBlue,
    main: colors.teal,
    dark: colors.navy,
    contrastText: colors.white,
  },
  background: {
    default: colors.beige,
    paper: colors.white,
    dark: colors.navy,
  },
  text: {
    primary: colors.navy,
    secondary: colors.teal,
    disabled: 'rgba(47, 65, 86, 0.38)',
  },
  divider: 'rgba(86, 124, 141, 0.12)',
  action: {
    active: colors.navy,
    hover: 'rgba(86, 124, 141, 0.08)',
    selected: 'rgba(86, 124, 141, 0.12)',
    disabled: 'rgba(47, 65, 86, 0.26)',
    disabledBackground: 'rgba(47, 65, 86, 0.12)',
  },
};

// Custom shadows for depth
const customShadows = [
  'none',
  '0px 2px 4px rgba(0, 0, 0, 0.05)',
  '0px 4px 8px rgba(0, 0, 0, 0.05)',
  '0px 8px 16px rgba(0, 0, 0, 0.05)',
  '0px 16px 24px rgba(0, 0, 0, 0.05)',
  '0px 24px 32px rgba(0, 0, 0, 0.05)',
];

// Typography configuration - SF Pro inspired
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"SF Pro Display"',
    '"SF Pro Text"',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.01em',
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
};

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        textTransform: 'none' as const,
        fontWeight: 500,
        padding: '10px 24px',
        fontSize: '1rem',
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(47, 65, 86, 0.15)',
          transform: 'translateY(-1px)',
        },
      },
      sizeLarge: {
        padding: '14px 32px',
        fontSize: '1.125rem',
      },
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.875rem',
      },
      contained: {
        '&:hover': {
          boxShadow: '0 6px 16px rgba(47, 65, 86, 0.2)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: '0px 4px 16px rgba(47, 65, 86, 0.08)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0px 8px 24px rgba(47, 65, 86, 0.12)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 16,
      },
      elevation1: {
        boxShadow: '0px 2px 8px rgba(47, 65, 86, 0.06)',
      },
      elevation2: {
        boxShadow: '0px 4px 12px rgba(47, 65, 86, 0.08)',
      },
      elevation3: {
        boxShadow: '0px 8px 24px rgba(47, 65, 86, 0.10)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 12,
          backgroundColor: colors.white,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.teal,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.navy,
              borderWidth: 2,
            },
          },
        },
        '& .MuiInputLabel-root': {
          color: colors.teal,
          '&.Mui-focused': {
            color: colors.navy,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 500,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 4,
        fontSize: '0.75rem',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid rgba(0, 0, 0, 0.05)',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: '2px 8px',
        '&.Mui-selected': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
          },
        },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      },
    },
  },
};

// Breakpoints configuration
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// Shape configuration
const shape = {
  borderRadius: 8,
};

// Spacing configuration (8px base)
const spacing = 8;

// Light theme configuration
const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: customColors.primary,
    secondary: customColors.secondary,
    success: customColors.success,
    warning: customColors.warning,
    error: customColors.error,
    info: customColors.info,
    background: {
      default: customColors.background.default,
      paper: customColors.background.paper,
    },
    text: customColors.text,
    divider: customColors.divider,
    action: customColors.action,
  },
  typography,
  components,
  breakpoints,
  shape,
  spacing,
};

// Dark theme configuration
const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      light: '#4fc3f7',
      main: '#29b6f6',
      dark: '#0288d1',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff8a80',
      main: '#ff5252',
      dark: '#c50e29',
      contrastText: '#fff',
    },
    background: {
      default: '#0a0e27',
      paper: '#1e1e2e',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: '#fff',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  typography,
  components: {
    ...components,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
  breakpoints,
  shape,
  spacing,
};

// Create themes
export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);

// Export default theme
export default lightTheme;

// Custom theme extensions
declare module '@mui/material/styles' {
  interface Theme {
    status?: {
      danger?: string;
      success?: string;
      warning?: string;
      info?: string;
    };
  }
  interface ThemeOptions {
    status?: {
      danger?: string;
      success?: string;
      warning?: string;
      info?: string;
    };
  }
}